import { API_V2_BASE_URL } from '../../config';
import { issueLogStreamToken } from '../../api/log';
import { SseEvent, SseFrameParser } from './sseFrameParser';

// 后端 JsonLogFormatter 的字段，未列出的字段按需自取
export interface StructuredLogLine {
  ts?: string;
  level?: string;
  logger?: string;
  instances?: string;
  message?: string;
  stack?: string;
  [key: string]: unknown;
}

export type LogStreamStatus = 'connecting' | 'streaming' | 'reconnecting' | 'stopped';

export interface LogStreamCallbacks {
  // 一批日志行与推进后的游标，游标为服务端不透明串
  onLines: (lines: StructuredLogLine[], cursor: string | undefined) => void;
  // 服务端的 error 事件表示日志源故障，连接本身仍由服务端维持
  onErrorEvent: (message: string) => void;
  onStatus: (status: LogStreamStatus) => void;
}

export interface LogStreamClientOptions {
  // 退避基数与上限，测试时注入小值
  retryBaseDelayMs?: number;
  retryMaxDelayMs?: number;
  // 每次连接尝试前读取当前游标，游标权威存放在 redux，客户端不自己持有状态
  getCursor?: () => string | undefined;
  // 便于测试注入，默认用全局 fetch
  fetchImpl?: typeof fetch;
}

export interface LogStreamClient {
  start: () => void;
  stop: () => void;
}

function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise(resolve => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * 项目日志流的 SSE 客户端。
 *
 * 注意，没用浏览器原生 EventSource，原因有二，一是 token 只有 60 秒，EventSource 的自动重连会复用过期
 * token 拿到 401，而按 SSE 规范非 200 会终止重连；二是原生 EventSource 无法自定义 header，手动重建连接
 * 带不了 Last-Event-ID，游标缺失时 file 源会从文件头全量重发。fetch 流两个问题都能解决，代价是自己解析帧。
 */
export function createLogStreamClient(
  projectId: number,
  callbacks: LogStreamCallbacks,
  options: LogStreamClientOptions = {},
): LogStreamClient {
  const baseDelay = options.retryBaseDelayMs ?? 1000;
  const maxDelay = options.retryMaxDelayMs ?? 10000;
  const doFetch: typeof fetch = options.fetchImpl ?? ((...args) => fetch(...args));

  let stopped = true;
  let controller: AbortController | null = null;
  // 游标在客户端留存，重连时经 header 原样带回，保证不丢行不重复
  let cursor: string | undefined;
  let attempt = 0;
  // 本次连接收到的日志行数，用于区分服务端积压保护的正常结束与真正的异常断流
  let linesInAttempt = 0;
  // 连续没有收到行就结束连接的次数
  let emptyCloseStreak = 0;

  function handleEvent(event: SseEvent): void {
    if (event.event === 'log') {
      let payload: { lines?: StructuredLogLine[] };
      try {
        payload = JSON.parse(event.data);
      } catch {
        // 解析不了就整条丢弃且不推进游标，重连后服务端会再推一次，宁可重复不可丢
        return;
      }
      const lines = Array.isArray(payload?.lines) ? payload.lines : [];
      if (event.id) {
        cursor = event.id;
      }
      if (lines.length > 0) {
        linesInAttempt += lines.length;
        callbacks.onLines(lines, cursor);
      }
      return;
    }

    if (event.event === 'error') {
      let message = '未知错误';
      try {
        const payload = JSON.parse(event.data);
        if (typeof payload?.message === 'string' && payload.message) {
          message = payload.message;
        }
      } catch {
        // 错误体解析失败也用默认提示，不额外打扰用户
      }
      callbacks.onErrorEvent(message);
    }
  }

  async function consume(body: ReadableStream<Uint8Array>): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    const parser = new SseFrameParser();
    for (;;) {
      const { value, done } = await reader.read();
      if (done) {
        return;
      }
      const events = parser.push(decoder.decode(value, { stream: true }));
      events.forEach(handleEvent);
    }
  }

  async function run(): Promise<void> {
    while (!stopped) {
      controller = new AbortController();
      const { signal } = controller;
      linesInAttempt = 0;
      // 服务端主动结束连接属于积压保护，不算错误，这里把它和真正的连接失败区分开
      let serverClosed = false;
      try {
        const knownCursor = options.getCursor?.();
        if (knownCursor) {
          cursor = knownCursor;
        }
        // 每次连接都换新 token，401 与过期两种情况一并覆盖
        const res = await issueLogStreamToken(projectId, signal);
        const streamRes = await doFetch(
          `${API_V2_BASE_URL}/log/stream?token=${encodeURIComponent(res.data.token)}`,
          {
            headers: cursor ? { 'Last-Event-ID': cursor } : {},
            signal,
          },
        );
        if (!streamRes.ok || !streamRes.body) {
          throw new Error(`日志流连接失败 HTTP ${streamRes.status}`);
        }
        attempt = 0;
        callbacks.onStatus('streaming');
        await consume(streamRes.body);
        serverClosed = true;
      } catch {
        // 网络故障、token 失效、非 2xx 都落在这里，与下面的正常结束共用重连路径
      }

      if (stopped || signal.aborted) {
        break;
      }

      // 本次连接收到过日志，说明是服务端消费不及时而正常结束，立即带游标续接。
      // 不报重连中也不等退避，否则首屏补拉期间会反复闪烁
      if (serverClosed && linesInAttempt > 0) {
        emptyCloseStreak = 0;
        continue;
      }

      emptyCloseStreak = serverClosed ? emptyCloseStreak + 1 : 0;

      // 连续空手结束超过阈值才当作异常，避免与偶发的一次空连接纠缠
      if (!serverClosed || emptyCloseStreak >= 3) {
        callbacks.onStatus('reconnecting');
      }
      await delay(Math.min(baseDelay * 2 ** attempt, maxDelay), signal);
      attempt += 1;
    }
  }

  return {
    start: () => {
      if (!stopped) {
        return;
      }
      stopped = false;
      attempt = 0;
      emptyCloseStreak = 0;
      callbacks.onStatus('connecting');
      void run();
    },
    stop: () => {
      if (stopped) {
        return;
      }
      stopped = true;
      controller?.abort();
      controller = null;
      callbacks.onStatus('stopped');
    },
  };
}
